/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneSheetHost.tsx
 * FILE_ID: JOBHUB-QA-PHONE-SHEETS-001
 * PURPOSE: One open-id sheet tree for the phone workspace. Frame stays a flex column.
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useIsMdUp, useQaPhotoPrefs, type QaNote, type QaStatus } from '@quotech/ui'
import type { QaAreaExecution, QaFloor, QaItemInstance } from '../../../qa/qaApi'
import { normalizeList, checklistGroups, type QaChecklistGroup } from '../../../qa/qaApiHelpers'
import { qaNeedsSignoff } from '../../../qa/qaHoldPoints'
import { QaQuoteSheet } from '../QaQuoteSheet'
import { qaDeleteConfirm, qaDeleteTargets, selectedQaUnion, uniqueIds } from '../qaItpScope'
import { QA_PENDING_DELETE_NOTICE } from '../qaTabSelectors'
import { applyQaAssign, applyQaDelete } from '../qaSelectionWrites'
import { useQaItpExecutions } from '../useQaItpExecutions'
import type { JobQaTabController } from '../useJobQaTabController'
import {
  buildPhoneRowMenuActions,
  phoneRowSiblingFlags,
  resolvePhoneRow,
} from './qaPhoneRowActions'
import type { ScopeRow } from './sheets/QaPhoneScopeSheet'
import { QaPhoneAssignSheet } from './sheets/QaPhoneAssignSheet'
import { QaPhoneConfirmSheet } from './sheets/QaPhoneConfirmSheet'
import { QaPhoneDensitySheet } from './sheets/QaPhoneDensitySheet'
import { QaPhoneMoreSheet } from './sheets/QaPhoneMoreSheet'
import { QaPhoneMoveSheet } from './sheets/QaPhoneMoveSheet'
import { QaPhoneBelongsToLevelSheet } from './sheets/QaPhoneBelongsToLevelSheet'
import { QaPhoneSelectQaSheet } from './sheets/QaPhoneSelectQaSheet'
import { QaPhoneStandardChecksSheet } from './sheets/QaPhoneStandardChecksSheet'
import { QaPhoneNewChecklistSheet } from './sheets/QaPhoneNewChecklistSheet'
import { QaPhoneNotesSheet } from './sheets/QaPhoneNotesSheet'
import { QaPhonePackSheet } from './sheets/QaPhonePackSheet'
import { QaPhoneRowActionsSheet } from './sheets/QaPhoneRowActionsSheet'
import { QaPhoneScopeSheet } from './sheets/QaPhoneScopeSheet'
import { QaPhoneStatusSheet } from './sheets/QaPhoneStatusSheet'
import { QaPhoneAddProductSheet } from './sheets/QaPhoneAddProductSheet'
import { QaPhoneAddSheet } from './sheets/QaPhoneAddSheet'
import { QaPhoneTemplatesSheet } from './sheets/QaPhoneTemplatesSheet'
import { QaPhoneItpLibrarySheet } from './sheets/QaPhoneItpLibrarySheet'
import type { QaPhoneUi } from './useQaPhoneUi'
import { requestQaPhonePage, type QaPhonePage } from './qaPhonePage'
import { rowMenuClip } from '../../../qa/qaClipboard'
import { qaErrorMessage } from '../qaTabSelectors'
import { addBlankChecklist } from '../addBlankChecklist'
import { useQaStructureMenu } from '../useQaStructureMenu'
import { useQaSiblingReorder } from '../useQaSiblingReorder'
import { addLocation } from './addPhoneLocation'

export function QaPhoneSheetHost({
  controller,
  ui,
  areaRows,
  levelRows,
  groups,
  areaIds,
  items,
  visibleIds,
  scope,
  notes,
  noteItem,
  execution,
  onRefresh,
  onExport,
  onSaveToLibrary,
  phonePage = 'qa',
  renameChecklistId,
  onRenameChecklistStarted,
}: {
  controller: JobQaTabController
  ui: QaPhoneUi
  areaRows: ScopeRow[]
  levelRows?: { id: string; name: string; empty: boolean }[]
  groups: QaChecklistGroup[]
  areaIds: string[]
  items: QaItemInstance[]
  visibleIds: string[]
  scope: 'areas' | 'checklists' | 'checks'
  notes: QaNote[]
  noteItem?: QaItemInstance
  execution?: QaAreaExecution
  onRefresh: () => void
  onExport: () => void
  onSaveToLibrary?: () => void
  phonePage?: QaPhonePage
  renameChecklistId?: string | null
  onRenameChecklistStarted?: () => void
}) {
  const canBuild = controller.canBuild !== false
  const queryClient = useQueryClient()
  const setOpenSheet = ui.setOpenSheet
  const isMdUp = useIsMdUp()
  useEffect(() => {
    if (canBuild && controller.templatesNonce > 0 && !isMdUp && controller.workspace.activeInspectionId) {
      setOpenSheet('templates')
    }
  }, [canBuild, controller.templatesNonce, controller.workspace.activeInspectionId, isMdUp, setOpenSheet])
  const { api, assignee, history, props, selection, tree, workspace } = controller
  const [rename, setRename] = useState<{ scope: 'areas' | 'checklists'; id: string } | null>(null)
  const [phoneDeleteNote, setPhoneDeleteNote] = useState<string | null>(null)
  const moves = useQaSiblingReorder(controller)
  const structure = useQaStructureMenu({
    api,
    jobId: props.jobId,
    inspectionId: workspace.activeInspectionId,
    setError: controller.setError,
    reorder: workspace.areaMode === 'build' ? moves : undefined,
    onCreated: (scope, id) => setRename({ scope, id }),
    onContainedIn: () => setOpenSheet('belongs-to-level'),
  })
  const photoPrefs = useQaPhotoPrefs()
  // `qa-areas`/`qa-floors` are shared cache keys. A stray payload shape must
  // render an empty list here, never throw past the error boundary and blank
  // the whole record (BUG-2026-09-16-QA-RECORD-FLOORS-CRASH).
  const areas = normalizeList<QaAreaExecution>(workspace.areasQuery.data)
  const { executions } = useQaItpExecutions(areaIds, api, workspace.areaExecutionsReady)
  const selected = selectedQaUnion(selection.visibleOf, executions, areaIds, selection.excludedChecks)
  const deleteTargets = qaDeleteTargets(
    // Reused from: qaItpScope.ts (selectedQaUnion)
    selected,
    executions,
  )
  const assignItemIds = uniqueIds([
    ...selected.checks,
    ...executions.flatMap((area) => checklistGroups(area)
      .filter((group) => selected.checklists.includes(group.checklist.id))
      .flatMap((group) => group.items.map((row) => row.id))),
  ])
  const jumpRows = executions.flatMap((area) => checklistGroups(area).map((group) => ({
    id: group.checklist.id,
    name: group.name,
    areaId: area.id,
    areaName: area.area_name,
  })))
  const target = resolvePhoneRow(items, ui.expandedRowId, visibleIds)
  const siblings = target ? phoneRowSiblingFlags(items, target) : { isFirst: true, isLast: true }
  const afterMutate = () => {
    workspace.invalidateExecution()
    ui.closeSheet()
  }
  const openTemplates = () => {
    // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useJobQaTabController.ts (requestOpenTemplates)
    controller.requestOpenTemplates()
    ui.closeSheet()
  }
  const addActions = {
    // Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankArea.ts (addBlankArea)
    addArea: () => addLocation(controller, ui.closeSheet),
    addChecklist: () => {
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankChecklist.ts (addBlankChecklist)
      if (!execution || execution.status === 'locked') return
      void addBlankChecklist(api, execution.id)
        .then((id) => {
          workspace.setAllChecklists(false)
          workspace.setActiveChecklistId(id)
          afterMutate()
        })
        .catch((err: unknown) => {
          controller.setError(err instanceof Error ? err.message : 'Could not add the checklist.')
        })
    },
    addProduct: () => ui.toggleSheet('add-product'),
    standardChecks: () => ui.toggleSheet('standard-checks'),
    addRow: () => {
      if (!execution) return
      void api.addItem(execution.id, {}, null, null, workspace.activeChecklistId ?? null)
        .then(afterMutate)
    },
    insertFromLibrary: workspace.activeChecklistId && execution ? () => { ui.closeSheet(); controller.requestOpenTemplates({ checklistId: workspace.activeChecklistId as string, areaId: execution.id, name: execution.checklists.find((row) => row.id === workspace.activeChecklistId)?.template_name ?? 'checklist' }) } : undefined,
  }
  const rowClip = target
    ? rowMenuClip(target, scope === 'checks' ? visibleIds : [], execution?.inspection_id ?? '')
    : null
  async function pastePhoneRow(position: 'above' | 'below') {
    if (!target || !rowClip?.sourceIds.length) return
    try {
      await api.pasteQaNodes({
        kind: 'rows',
        source_ids: rowClip.sourceIds,
        target_id: target.checklist_instance,
        anchor_id: target.id,
        position,
      })
      afterMutate()
    } catch (error) {
      controller.setError(qaErrorMessage(error, 'Could not paste those rows.'))
    }
  }
  const rowMenu = {
    ...buildPhoneRowMenuActions(
      api,
      execution?.id,
      items,
      target,
      workspace.activeChecklistId ?? null,
      afterMutate,
      queryClient,
      execution ?? null,
      workspace.activeInspectionId,
    ),
    onCopy: rowClip?.onCopy,
    onPasteAbove: () => { void pastePhoneRow('above') },
    onPasteBelow: () => { void pastePhoneRow('below') },
    onUndo: history.canUndo ? () => { void history.undo() } : undefined,
    onRedo: history.canRedo ? () => { void history.redo() } : undefined,
    copyCount: rowClip?.copyCount,
    pasteLabel: rowClip?.pasteLabel ?? null,
  }
  return (
    <>
      <QaPhoneMoreSheet
        open={ui.openSheet === 'more'}
        onClose={ui.closeSheet}
        onOpen={(id) => {
          if (id === 'templates') controller.consumeSetupWrite?.()
          ui.toggleSheet(id)
        }}
        allChecklists={workspace.allChecklists}
        buildView={ui.buildView}
        onToggleBuildView={workspace.areaMode === 'build' ? ui.toggleBuildView : undefined}
        onToggleAllChecklists={() => workspace.setAllChecklists(!workspace.allChecklists)}
        canUndo={Boolean(history?.canUndo)}
        canRedo={Boolean(history?.canRedo)}
        onUndo={() => void history?.undo()}
        onRedo={() => void history?.redo()}
        selectPhotos={photoPrefs.selectEnabled}
        onToggleSelectPhotos={() => photoPrefs.setSelectEnabled(!photoPrefs.selectEnabled)}
        onRefresh={onRefresh}
        onSelect={() => { ui.setSelecting((on) => !on); ui.closeSheet() }}
        addActions={addActions}
        onShowTable={() => {
          workspace.setAreaMode('build')
          ui.closeSheet()
        }}
        jobSections={props.phoneNav?.tabs}
        onJobSection={props.phoneNav ? (key) => {
          props.phoneNav?.onTab(key)
          ui.closeSheet()
        } : undefined}
        onExport={() => { ui.closeSheet(); onExport() }}
        onSaveToLibrary={onSaveToLibrary}
        canBuild={canBuild}
      />
      <QaPhoneItpLibrarySheet
        open={ui.openSheet === 'itp-library'}
        onClose={ui.closeSheet}
        jobId={props.jobId}
        orgId={props.orgId}
      />
      {canBuild ? (
        <QaPhoneAddSheet
          open={ui.openSheet === 'add'}
          onClose={ui.closeSheet}
          actions={addActions}
        />
      ) : null}
      <QaPhoneScopeSheet
        open={ui.openSheet === 'scope-area'}
        onClose={ui.closeSheet}
        title="Location"
        rows={areaRows}
        currentId={workspace.activeAreaId}
        selecting={ui.selecting}
        selectedIds={new Set(selection.visibleOf('areas', areaIds))}
        onToggleSelectMode={() => ui.setSelecting((on) => !on)}
        onPick={(id) => { workspace.selectArea(id); ui.closeSheet() }}
        onToggle={(id) => selection.toggle('areas', id)}
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/addBlankArea.ts (addBlankArea)
        onAdd={canBuild ? () => addLocation(controller, ui.closeSheet) : undefined}
        addLabel="Add Location"
        onRename={canBuild ? (id, name) => api.renameArea(id, name).then(afterMutate) : undefined}
        menuFor={canBuild ? (rowId) => {
          const row = areaRows.find((item) => item.id === rowId)
          return row ? structure.itemsForArea({ id: row.id, area_name: row.name }) : []
        } : undefined}
        renameId={rename?.scope === 'areas' ? rename.id : null}
        onRenameStarted={() => setRename(null)}
        levelRows={levelRows}
        onOpenLevel={canBuild ? (id, point) => {
          const level = levelRows?.find((row) => row.id === id)
          if (level) structure.openLevel({ id: level.id, floor_name: level.name }, point)
        } : undefined}
      />
      <QaPhoneScopeSheet
        open={ui.openSheet === 'scope-checklist'}
        onClose={ui.closeSheet}
        title="Checklist"
        rows={groups.map((g) => ({ id: g.checklist.id, name: g.name }))}
        currentId={workspace.activeChecklistId}
        jumpRows={jumpRows}
        currentAreaId={workspace.activeAreaId}
        onJump={(areaId, checklistId) => {
          workspace.selectArea(areaId)
          workspace.setAllChecklists(false)
          workspace.setActiveChecklistId(checklistId)
          ui.closeSheet()
        }}
        selecting={ui.selecting}
        selectedIds={new Set(selection.visibleOf('checklists', groups.map((g) => g.checklist.id)))}
        onToggleSelectMode={() => ui.setSelecting((on) => !on)}
        onPick={(id) => {
          workspace.setAllChecklists(false)
          workspace.setActiveChecklistId(id)
          ui.closeSheet()
        }}
        onToggle={(id) => selection.toggle('checklists', id)}
        onAdd={canBuild ? openTemplates : undefined}
        addLabel="Add checklist"
        onRename={canBuild ? (id, name) => api.renameChecklist(id, name).then(afterMutate) : undefined}
        menuFor={canBuild && workspace.activeAreaId ? (rowId) => {
          const group = groups.find((item) => item.checklist.id === rowId)
          if (!group) return []
          return structure.itemsForChecklist(group.checklist.id, group.name, workspace.activeAreaId as string)
        } : undefined}
        renameId={rename?.scope === 'checklists' ? rename.id : renameChecklistId ?? null}
        onRenameStarted={() => { setRename(null); onRenameChecklistStarted?.() }}
      />
      <QaQuoteSheet
        open={ui.openSheet === 'scope-quote'}
        onClose={ui.closeSheet}
        workspace={workspace}
        jobId={props.jobId}
      />
      <QaPhoneAssignSheet
        open={ui.openSheet === 'assign'}
        onClose={ui.closeSheet}
        candidates={assignee.candidates}
        disabledReason={scope === 'checklists'
          ? 'Checklists have no assignee of their own. Assign the area or the checks.'
          : undefined}
        onAssign={(userId, dueDate, name) => {
          if (scope === 'checklists' || userId == null) return
          if (selected.areas.length === 0 && assignItemIds.length === 0) return
          void applyQaAssign({
            queryClient,
            inspectionId: workspace.activeInspectionId,
            executions,
            areaIds: selected.areas,
            itemIds: assignItemIds,
            person: { userId, name: name || '', dueDate: dueDate || '' },
          }).then((held) => {
            workspace.bulkAssign.mutate({
              ...(selected.areas.length ? { area_ids: selected.areas } : {}),
              ...(assignItemIds.length ? { item_ids: assignItemIds } : {}),
              user_id: userId,
              ...(dueDate ? { due_date: dueDate } : {}),
            }, {
              onSuccess: () => held.release(),
              onError: () => { held.restore(); held.release() },
            })
          })
        }}
      />
      <QaPhoneStatusSheet
        open={ui.openSheet === 'status'}
        onClose={ui.closeSheet}
        value={noteItem?.status ?? 'not_started'}
        onChange={(status: QaStatus) => {
          if (noteItem) workspace.setItemStatus(noteItem.id, status)
        }}
      />
      <QaPhoneNotesSheet
        open={ui.openSheet === 'notes'}
        onClose={ui.closeSheet}
        notes={notes}
        checkName={noteItem?.title}
        onAdd={async (text) => {
          if (!execution || !noteItem) return
          await api.addNoteEvidence({ areaId: execution.id, itemInstanceId: noteItem.id, noteText: text })
          workspace.invalidateExecution()
        }}
        onDelete={async (id) => {
          await api.deleteEvidence(id)
          workspace.invalidateExecution()
        }}
      />
      {canBuild ? <QaPhoneMoveSheet
        open={ui.openSheet === 'move'}
        onClose={ui.closeSheet}
        count={visibleIds.length}
        label={scope === 'checklists' ? 'checklists' : scope === 'checks' ? 'checks' : 'areas'}
        targets={scope === 'areas'
          // A stray cache shape under `qa-floors` must render an empty move
          // list, never throw past the error boundary and blank the record.
          ? normalizeList<QaFloor>(workspace.floorsQuery.data)
            .map((floor) => ({ id: floor.id, name: floor.floor_name }))
          : areas.filter((area) => area.id !== execution?.id).map((area) => ({ id: area.id, name: area.area_name }))}
        allowNewLevel={scope === 'areas'}
        busy={tree.move.isPending}
        error={tree.error}
        onMove={(destination) => {
          tree.move.mutate({
            nodeType: scope === 'areas' ? 'area' : 'checklist_instance',
            nodeIds: scope === 'areas' ? selected.areas : selected.checklists,
            destination,
          }, { onSuccess: () => { ui.closeSheet(); selection.clear() } })
        }}
      /> : null}
      {canBuild ? <QaPhoneRowActionsSheet
        open={ui.openSheet === 'row-actions'}
        onClose={ui.closeSheet}
        locked={execution?.status === 'locked'}
        isFirst={siblings.isFirst}
        isLast={siblings.isLast}
        hasHold={Boolean(target?.has_hold_point)}
        needsSignoff={target ? qaNeedsSignoff(target) : false}
        {...rowMenu}
      /> : null}
      {canBuild && execution ? (
        <QaPhoneNewChecklistSheet
          open={ui.openSheet === 'new-checklist'}
          onClose={ui.closeSheet}
          areaId={execution.id}
          api={api}
          client={controller.client}
          onCreated={(id) => {
            workspace.setAllChecklists(false)
            workspace.setActiveChecklistId(id)
            ui.closeSheet()
          }}
          onMutated={workspace.invalidateExecution}
        />
      ) : null}
      {workspace.activeInspectionId && (
        <QaPhonePackSheet
          open={ui.openSheet === 'pack'}
          onClose={ui.closeSheet}
          inspectionId={workspace.activeInspectionId}
          api={api}
        />
      )}
      {canBuild ? <QaPhoneTemplatesSheet
        open={ui.openSheet === 'templates'}
        onClose={ui.closeSheet}
        controller={controller}
        onInserted={phonePage === 'setup' ? () => {
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhonePage.ts (requestQaPhonePage)
          requestQaPhonePage('qa', true)
          ui.closeSheet()
        } : undefined}
      /> : null}
      {canBuild && workspace.activeInspectionId ? (
        <QaPhoneAddProductSheet
          open={ui.openSheet === 'add-product'}
          onClose={ui.closeSheet}
          controller={controller}
        />
      ) : null}
      {canBuild ? (
        <QaPhoneStandardChecksSheet
          open={ui.openSheet === 'standard-checks'}
          onClose={ui.closeSheet}
          controller={controller}
        />
      ) : null}
      <QaPhoneSelectQaSheet
        open={ui.openSheet === 'select-qa'}
        onClose={ui.closeSheet}
        controller={controller}
      />
      <QaPhoneBelongsToLevelSheet
        open={ui.openSheet === 'belongs-to-level'}
        onClose={ui.closeSheet}
        controller={controller}
      />
      <QaPhoneDensitySheet open={ui.openSheet === 'density'} onClose={ui.closeSheet} />
      {canBuild ? <QaPhoneConfirmSheet
        open={ui.openSheet === 'confirm-delete'}
        onClose={() => { setPhoneDeleteNote(null); ui.closeSheet() }}
        title="Delete"
        body={phoneDeleteNote ?? qaDeleteConfirm(deleteTargets)}
        confirmLabel="Delete"
        onConfirm={async () => {
          const result = await applyQaDelete({
            api,
            queryClient,
            inspectionId: workspace.activeInspectionId,
            executions,
            targets: deleteTargets,
          })
          if (workspace.activeAreaId && result.deletedAreas.includes(workspace.activeAreaId)) {
            const list = normalizeList<QaAreaExecution>(queryClient.getQueryData(['qa-areas', workspace.activeInspectionId]))
            workspace.selectArea(list[0]?.id ?? null)
          }
          if (result.failed > 0) {
            setPhoneDeleteNote('Could not delete.')
            return false
          }
          if (result.pendingItems.length > 0) {
            setPhoneDeleteNote(QA_PENDING_DELETE_NOTICE)
            for (const id of result.deletedItems) {
              if (selection.isSelected('checks', id)) selection.toggle('checks', id)
            }
            for (const id of result.deletedChecklists) {
              if (selection.isSelected('checklists', id)) selection.toggle('checklists', id)
            }
            for (const id of result.deletedAreas) {
              if (selection.isSelected('areas', id)) selection.toggle('areas', id)
            }
            return false
          }
          selection.clear()
          return true
        }}
      /> : null}
    </>
  )
}
